import { ArrowLeft, RotateCcw, AlertTriangle, Wand2, MonitorSmartphone, FileCode2, Shield, Image, Package, Globe, Trophy, Languages } from "lucide-react";
import { useTranslation } from 'react-i18next';
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useIsMobile } from "@/hooks/useIsMobile";
import { useNavigate } from "react-router-dom";
import { useAppContext } from "@/hooks/useAppContext";
import { DEFAULT_SYSTEM_PROMPT_TEMPLATE, DEFAULT_SYSTEM_PROMPT_TEMPLATE_VANILLA } from "@/lib/system";
import { DEFAULT_AGENTS_FILE } from "@/lib/defaultAgentsFile";
import { AVAILABLE_LANGUAGES, type LanguageCode } from "@/contexts/AppContext";

export function SystemSettings() {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { config, updateConfig } = useAppContext();
  const [esmUrlInput, setEsmUrlInput] = useState(config.esmUrl);
  const [corsProxyInput, setCorsProxyInput] = useState(config.corsProxy);
  const [faviconUrlInput, setFaviconUrlInput] = useState(config.faviconUrl);
  const [previewDomainInput, setPreviewDomainInput] = useState(config.previewDomain);
  const [showcaseModeratorInput, setShowcaseModeratorInput] = useState(config.showcaseModerator);
  const [systemPromptTemplateInput, setSystemPromptTemplateInput] = useState(config.systemPromptTemplate ?? DEFAULT_SYSTEM_PROMPT_TEMPLATE);
  const [systemPromptTemplateVanillaInput, setSystemPromptTemplateVanillaInput] = useState(config.systemPromptTemplateVanilla ?? DEFAULT_SYSTEM_PROMPT_TEMPLATE_VANILLA);
  const [agentsFileTemplateInput, setAgentsFileTemplateInput] = useState(config.agentsFileTemplate ?? DEFAULT_AGENTS_FILE);

  return (
    <div className="p-6 space-y-8">
      {isMobile && (
        <div className="space-y-1">
          <Button variant="ghost" size="sm" onClick={() => navigate('/settings')} className="h-8 px-2 -ml-2 text-muted-foreground">
            <ArrowLeft className="h-4 w-4 mr-1" />
            {t('settings')}
          </Button>
        </div>
      )}

      <div>
        <h1 className="text-xl font-semibold tracking-tight">{t('systemSettings')}</h1>
        <p className="text-sm text-muted-foreground mt-1">{t('systemSettingsDescription')}</p>
      </div>

      <div className="space-y-3 max-w-lg">
        <Accordion type="single" collapsible className="space-y-2">
          {/* System Prompt Template (UI + Superpowers) */}
          <AccordionItem value="system-prompt" className="border rounded-lg bg-card">
            <AccordionTrigger className="px-4 py-3 hover:no-underline">
              <div className="flex items-center gap-3">
                <Wand2 className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">{t('systemPromptTemplateSuperpowers')}</span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-4 pb-4 space-y-3">
              <p className="text-xs text-muted-foreground mb-2">{t('systemPromptTemplateSuperpowersDescription')}</p>
              <Textarea
                placeholder={t('systemPromptTemplatePlaceholder')}
                value={systemPromptTemplateInput}
                onChange={(e) => {
                  setSystemPromptTemplateInput(e.target.value);
                  updateConfig((c) => ({ ...c, systemPromptTemplate: e.target.value || undefined }));
                }}
                className="min-h-[400px] font-mono text-xs"
              />
              <div className="flex items-center justify-end">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setSystemPromptTemplateInput(DEFAULT_SYSTEM_PROMPT_TEMPLATE);
                    updateConfig((c) => ({ ...c, systemPromptTemplate: DEFAULT_SYSTEM_PROMPT_TEMPLATE }));
                  }}
                  className="gap-1 text-xs"
                >
                  <RotateCcw className="h-3 w-3" />
                  {t('resetToDefault')}
                </Button>
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* System Prompt Template (Just UI) */}
          <AccordionItem value="system-prompt-vanilla" className="border rounded-lg bg-card">
            <AccordionTrigger className="px-4 py-3 hover:no-underline">
              <div className="flex items-center gap-3">
                <MonitorSmartphone className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">{t('systemPromptTemplateJustUI')}</span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-4 pb-4 space-y-3">
              <p className="text-xs text-muted-foreground mb-2">{t('systemPromptTemplateJustUIDescription')}</p>
              <Textarea
                placeholder={t('systemPromptTemplateJustUIPlaceholder')}
                value={systemPromptTemplateVanillaInput}
                onChange={(e) => {
                  setSystemPromptTemplateVanillaInput(e.target.value);
                  updateConfig((c) => ({ ...c, systemPromptTemplateVanilla: e.target.value || undefined }));
                }}
                className="min-h-[400px] font-mono text-xs"
              />
              <div className="flex items-center justify-end">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setSystemPromptTemplateVanillaInput(DEFAULT_SYSTEM_PROMPT_TEMPLATE_VANILLA);
                    updateConfig((c) => ({ ...c, systemPromptTemplateVanilla: DEFAULT_SYSTEM_PROMPT_TEMPLATE_VANILLA }));
                  }}
                  className="gap-1 text-xs"
                >
                  <RotateCcw className="h-3 w-3" />
                  {t('resetToDefault')}
                </Button>
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* Agents File Template */}
          <AccordionItem value="agents-file" className="border rounded-lg bg-card">
            <AccordionTrigger className="px-4 py-3 hover:no-underline">
              <div className="flex items-center gap-3">
                <FileCode2 className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">Agents File (AGENTS.md)</span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-4 pb-4 space-y-3">
              <Alert variant="destructive" className="bg-amber-500/10 border-amber-500/50 text-amber-700 dark:text-amber-400">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription className="text-xs">
                  Modifying this file may affect how the AI understands and works with Nostr applications. Only edit if you know what you're doing.
                </AlertDescription>
              </Alert>
              <p className="text-xs text-muted-foreground">Customize the AGENTS.md file that provides context to the AI about your project. This file helps the AI understand your project's technology stack, conventions, and best practices.</p>
              <Textarea
                placeholder="Enter your custom AGENTS.md content..."
                value={agentsFileTemplateInput}
                onChange={(e) => {
                  setAgentsFileTemplateInput(e.target.value);
                  updateConfig((c) => ({ ...c, agentsFileTemplate: e.target.value || undefined }));
                }}
                className="min-h-[400px] font-mono text-xs"
              />
              <div className="flex items-center justify-end">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setAgentsFileTemplateInput(DEFAULT_AGENTS_FILE);
                    updateConfig((c) => ({ ...c, agentsFileTemplate: DEFAULT_AGENTS_FILE }));
                  }}
                  className="gap-1 text-xs"
                >
                  <RotateCcw className="h-3 w-3" />
                  {t('resetToDefault')}
                </Button>
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* CORS Proxy */}
          <AccordionItem value="cors-proxy" className="border rounded-lg bg-card">
            <AccordionTrigger className="px-4 py-3 hover:no-underline">
              <div className="flex items-center gap-3">
                <Shield className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">{t('corsProxy')}</span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-4 pb-4 space-y-3">
              <Input
                type="url"
                placeholder="https://proxy.shakespeare.diy/?url={href}"
                value={corsProxyInput}
                onChange={(e) => { setCorsProxyInput(e.target.value); updateConfig((c) => ({ ...c, corsProxy: e.target.value })); }}
                className="h-9"
              />
              <p className="text-xs text-muted-foreground">{t('corsProxyDescription')}</p>
            </AccordionContent>
          </AccordionItem>

          {/* Favicon URL */}
          <AccordionItem value="favicon-url" className="border rounded-lg bg-card">
            <AccordionTrigger className="px-4 py-3 hover:no-underline">
              <div className="flex items-center gap-3">
                <Image className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">Favicon URL</span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-4 pb-4 space-y-3">
              <Input
                type="url"
                placeholder="https://external-content.duckduckgo.com/ip3/{hostname}.ico"
                value={faviconUrlInput}
                onChange={(e) => { setFaviconUrlInput(e.target.value); updateConfig((c) => ({ ...c, faviconUrl: e.target.value })); }}
                className="h-9"
              />
              <p className="text-xs text-muted-foreground">URL template for fetching favicons. Variables: {'{hostname}'}, {'{origin}'}, {'{href}'}</p>
            </AccordionContent>
          </AccordionItem>

          {/* ESM CDN */}
          <AccordionItem value="esm-url" className="border rounded-lg bg-card">
            <AccordionTrigger className="px-4 py-3 hover:no-underline">
              <div className="flex items-center gap-3">
                <Package className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">{t('esmUrl')}</span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-4 pb-4 space-y-3">
              <Input
                type="url"
                placeholder="https://esm.shakespeare.diy"
                value={esmUrlInput}
                onChange={(e) => { setEsmUrlInput(e.target.value); updateConfig((c) => ({ ...c, esmUrl: e.target.value.replace(/\/+$/, '') })); }}
                className="h-9"
              />
              <p className="text-xs text-muted-foreground">{t('esmUrlDescription')}</p>
            </AccordionContent>
          </AccordionItem>

          {/* Preview Domain */}
          <AccordionItem value="preview-domain" className="border rounded-lg bg-card">
            <AccordionTrigger className="px-4 py-3 hover:no-underline">
              <div className="flex items-center gap-3">
                <Globe className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">{t('previewDomain')}</span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-4 pb-4 space-y-3">
              <Input
                type="text"
                placeholder="local-shakespeare.dev"
                value={previewDomainInput}
                onChange={(e) => { setPreviewDomainInput(e.target.value); updateConfig((c) => ({ ...c, previewDomain: e.target.value })); }}
                className="h-9"
              />
              <p className="text-xs text-muted-foreground">{t('previewDomainDescription')}</p>
            </AccordionContent>
          </AccordionItem>

          {/* Showcase */}
          <AccordionItem value="showcase" className="border rounded-lg bg-card">
            <AccordionTrigger className="px-4 py-3 hover:no-underline">
              <div className="flex items-center gap-3">
                <Trophy className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">Showcase</span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-4 pb-4 space-y-4">
              <div className="flex items-center justify-between">
                <Label htmlFor="showcase-enabled" className="text-sm">Showcase Enabled</Label>
                <Switch id="showcase-enabled" checked={config.showcaseEnabled} onCheckedChange={(checked) => updateConfig((c) => ({ ...c, showcaseEnabled: checked }))} />
              </div>
              <p className="text-xs text-muted-foreground">Display the app showcase section on the home page</p>
              <div className="space-y-2">
                <Label className="text-sm">Showcase Moderator</Label>
                <Input
                  type="text"
                  placeholder="npub1..."
                  value={showcaseModeratorInput}
                  onChange={(e) => { setShowcaseModeratorInput(e.target.value); updateConfig((c) => ({ ...c, showcaseModerator: e.target.value })); }}
                  className="h-9"
                />
                <p className="text-xs text-muted-foreground">Nostr public key (npub) of the showcase moderator</p>
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* Auto-Translate */}
          <AccordionItem value="auto-translate" className="border rounded-lg bg-card">
            <AccordionTrigger className="px-4 py-3 hover:no-underline">
              <div className="flex items-center gap-3">
                <Languages className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">Auto-Translate</span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-4 pb-4 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="auto-translate-enabled" className="text-sm">Enable Auto-Translate</Label>
                  <p className="text-xs text-muted-foreground mt-0.5">Automatically translate new UI text to selected languages</p>
                </div>
                <Switch
                  id="auto-translate-enabled"
                  checked={config.autoTranslate ?? false}
                  onCheckedChange={(checked) => updateConfig((c) => ({ ...c, autoTranslate: checked }))}
                />
              </div>

              {config.autoTranslate && (
                <div className="space-y-3 pt-2 border-t">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm">Target Languages</Label>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 text-xs"
                      onClick={() => {
                        const allCodes = AVAILABLE_LANGUAGES.map(l => l.code);
                        const currentLangs = config.autoTranslateLanguages ?? [];
                        const allSelected = allCodes.every(code => currentLangs.includes(code));
                        updateConfig((c) => ({
                          ...c,
                          autoTranslateLanguages: allSelected ? [] : allCodes
                        }));
                      }}
                    >
                      {(config.autoTranslateLanguages?.length ?? 0) === AVAILABLE_LANGUAGES.length
                        ? 'Deselect All'
                        : 'Select All'}
                    </Button>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {AVAILABLE_LANGUAGES.map((lang) => {
                      const isSelected = config.autoTranslateLanguages?.includes(lang.code) ?? false;
                      return (
                        <label
                          key={lang.code}
                          className="flex items-center gap-2 p-2 rounded-md border bg-background hover:bg-accent/50 cursor-pointer transition-colors"
                        >
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={(checked) => {
                              const currentLangs = config.autoTranslateLanguages ?? [];
                              const newLangs = checked
                                ? [...currentLangs, lang.code]
                                : currentLangs.filter(c => c !== lang.code);
                              updateConfig((c) => ({ ...c, autoTranslateLanguages: newLangs as LanguageCode[] }));
                            }}
                          />
                          <span className="text-base">{lang.flag}</span>
                          <span className="text-sm">{lang.name}</span>
                        </label>
                      );
                    })}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    When enabled, the AI will translate new UI strings to the selected languages when adding features.
                  </p>
                </div>
              )}
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>
    </div>
  );
}

export default SystemSettings;
